import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import { optimizedImageUrl } from '@/lib/imageOptimizer';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatPrice } from '@/lib/formatPrice';
import { CompactContactActions } from '@/components/property/CompactContactActions';

interface Props {
  title: string;
  icon?: React.ReactNode;
  properties: any[];
  emptyHidden?: boolean;
  cardWidthClass?: string;
}

export function PropertyScrollRow({ title, icon, properties, emptyHidden = true, cardWidthClass = 'w-[calc(50%-6px)] sm:w-[240px]' }: Props) {
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const { displayCurrency, rate } = useCurrency();

  if (emptyHidden && (!properties || properties.length === 0)) return null;

  const scroll = (dir: 'left' | 'right') => {
    if (!ref.current) return;
    const w = ref.current.clientWidth * 0.6;
    ref.current.scrollBy({ left: dir === 'left' ? -w : w, behavior: 'smooth' });
  };

  return (
    <section className="container py-6 md:py-8">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {icon}
          <h2 className="font-display text-lg md:text-2xl font-bold">{title}</h2>
        </div>
      </div>
      <div className="relative">
        <button onClick={() => scroll('left')} aria-label={`Scroll ${title} left`} className="absolute -left-2 top-[35%] -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-background/90 border shadow flex items-center justify-center">
          <ChevronLeft className="h-4 w-4 text-primary" />
        </button>
        <button onClick={() => scroll('right')} aria-label={`Scroll ${title} right`} className="absolute -right-2 top-[35%] -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-background/90 border shadow flex items-center justify-center">
          <ChevronRight className="h-4 w-4 text-primary" />
        </button>
        <div ref={ref} className="flex gap-3 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-2 scroll-smooth">
          {properties.map((p: any) => {
            const title = typeof p.title === 'object' ? p.title?.en || '' : p.title || '';
            const img = p.property_images?.find((i: any) => i.is_primary)?.url || p.property_images?.[0]?.url;
            const src = optimizedImageUrl(img || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=400', { width: 400, height: 300, quality: 75 });
            return (
              <div key={p.id} onClick={() => navigate(`/property/${p.id}`)}
                className={`snap-start flex-shrink-0 ${cardWidthClass} rounded-xl border bg-card overflow-hidden hover:shadow-lg cursor-pointer group`}>
                <div className="relative aspect-[4/3]">
                  <img src={src} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <span className="absolute top-2 left-2 px-2 py-0.5 bg-primary text-primary-foreground text-[10px] font-bold rounded uppercase">
                    {p.listing_type === 'rent' ? 'RENT' : 'SALE'}
                  </span>
                </div>
                <div className="p-2.5 space-y-1">
                  <div className="font-bold text-primary text-sm">{formatPrice(p.price, p.currency || 'ETB', displayCurrency, rate)}</div>
                  <div className="font-semibold text-xs leading-tight truncate">{title}</div>
                  <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                    <MapPin className="h-3 w-3 shrink-0" />
                    <span className="truncate">{p.city}{p.sub_city ? `, ${p.sub_city}` : ''}</span>
                  </div>
                  <div className="pt-2 mt-1 border-t">
                    <CompactContactActions
                      phone={p.contact_phone}
                      agentName={p.contact_person}
                      agencyName={p.agency_name}
                      agentLogo={p.agency_logo_url}
                      referenceId={p.reference_code || `ID${String(p.id).slice(0,8)}`}
                      propertyId={p.id}
                      listingType="property"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
